import { type Ranking, type RankingInfo, rankItem } from "@tanstack/match-sorter-utils";
import { type FC, type ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { cn } from "../../lib/utils";
import { Icon } from "../../primitives";
import { Button } from "../../ui/Button";
import { SearchInput } from "../../ui/SearchInput";

export type SearchableSelectListItem = {
	value: string;
	element: ReactNode;
	searchValues: string[];
};

export type SearchableSelectListInputProps = {
	value: string | null | undefined;
	listItems: SearchableSelectListItem[];
	selectedListItemElement?: ReactNode;
	searchPlaceholder?: string;
	listMaxHeight?: string;
	hasError?: boolean;
	onChange: (value: string) => void;
	onBlur: () => void;
};

const getListItemRank = (listItem: SearchableSelectListItem, searchString: string) => {
	const searchValuesWithRank = listItem.searchValues.map((value, index) => {
		const ranking = rankItem(value, searchString);
		const companyNameRanking = ranking.rank < 7 ? ((ranking.rank + 1) as Ranking) : ranking.rank;
		return {
			value,
			rank: index === 0 && ranking.passed ? { ...ranking, rank: companyNameRanking } : ranking,
		};
	});
	return {
		...listItem,
		searchRank: searchValuesWithRank.sort((a, b) => (a.rank.rank > b.rank.rank ? -1 : 1))[0].rank,
	};
};

const fuzzyMatchAndSort = (listItems: SearchableSelectListItem[], searchString: string) => {
	return listItems
		.map<SearchableSelectListItem & { searchRank: RankingInfo }>((listItem) => ({
			...listItem,
			searchRank: getListItemRank(listItem, searchString).searchRank,
		}))
		.filter((listItem) => listItem.searchRank.passed)
		.sort((a, b) => (a.searchRank.rank > b.searchRank.rank ? -1 : 1));
};

export const SearchableSelectListInput: FC<SearchableSelectListInputProps> = ({
	value,
	listItems,
	selectedListItemElement,
	searchPlaceholder,
	listMaxHeight,
	hasError,
	onChange,
	onBlur,
}) => {
	const [searchString, setSearchString] = useState("");
	const filteredListItems = useMemo(
		() => fuzzyMatchAndSort(listItems, searchString),
		[listItems, searchString],
	);
	const scrollRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (scrollRef.current) {
			scrollRef.current.scrollTop = 0;
		}
	}, [scrollRef]);

	useEffect(() => {
		onBlur();
	}, [value]);

	const hasSelection = !!value;

	if (hasSelection) {
		return (
			<div className="w-full flex flex-col space-y-[24px] min-h-fit mt-[7px]">
				<div className="shrink-0 w-full flex items-center rounded-md cursor-pointer outline outline-brand-accent outline-[2px] outline-offset-[-2px] bg-brand-accent/[.06]">
					<div className="flex h-full pl-[25px] items-center justify-center">
						<div className="relative aspect-square h-4 w-4 rounded-full border border-bc-strong bg-surface-base focus:outline-none flex items-center justify-center">
							<div className="absolute top-[-1px] left-[-1px] aspect-square h-4 w-4 rounded-full border-[6px] border-brand-accent ml-[-1] mt-[-1]" />
						</div>
					</div>
					<div className="h-full flex-1">
						{selectedListItemElement ?? listItems.find((item) => item.value === value)?.element}
					</div>
				</div>
				<Button
					onClick={() => onChange("")}
					className="ml-[-4px] w-fit h-[34px] bg-surface-neutral/90"
					color="secondary"
					thinFont
				>
					<div className="flex min-w-fit items-center space-x-[8px]">
						<Icon type="x-mark" size="micro" />
						<div className="min-w-fit text-[13.5px] font-medium">Clear Selection</div>
					</div>
				</Button>
			</div>
		);
	}

	return (
		<div
			className={cn(
				"flex flex-col w-full max-h-full space-y-[20px]",
				hasError ? "outline outline-danger outline-offset-[6px] rounded-md" : undefined,
			)}
		>
			<div className="flex items-center w-full min-h-fit">
				<SearchInput
					className="w-full outline-bc-default outline outline-[1px] outline-offset-[-1px]"
					placeholder={searchPlaceholder ?? "Search..."}
					onChange={setSearchString}
				/>
			</div>
			{filteredListItems.length ? (
				<div className="relative w-full h-fit max-h-full flex-1 flex flex-col">
					<div className="absolute left-0 right-0 top-0 w-full h-[1px] bg-bc-default z-10" />
					<div
						ref={scrollRef}
						className="relative w-full h-fit max-h-full flex-1 overflow-y-auto"
						style={listMaxHeight ? { maxHeight: listMaxHeight } : undefined}
					>
						{filteredListItems.map((listItem) => (
							<div
								key={listItem.value}
								className="shrink-0 relative group w-full border-b border-x border-bc-default flex items-center cursor-pointer mb-[-1px] last:mb-[0px] z-20 hover:outline hover:outline-bc-strong hover:outline-[1px] hover:outline-offset-[-1px] hover:bg-surface-neutral"
								onClick={() => onChange(listItem.value)}
							>
								<div className="flex h-full pl-[25px] items-center justify-center">
									<div className="relative aspect-square h-4 w-4 rounded-full border border-bc-strong bg-surface-base focus:outline-none flex items-center justify-center" />
								</div>
								<div className="h-full flex-1">{listItem.element}</div>
							</div>
						))}
					</div>
					<div className="absolute left-0 right-0 bottom-0 w-full h-[1px] bg-bc-default z-10" />
				</div>
			) : (
				<div className="w-full bg-surface-base border border-bc-default px-[25px] py-[15px] text-sm text-tertiary flex justify-center italic">
					No results
				</div>
			)}
		</div>
	);
};
